<script setup lang="ts">
import { computed, toRef } from "vue";
import draggable from "vuedraggable";
import { GripVertical, Plus, X } from "lucide-vue-next";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/episto-ui/select";
import PopulationField from "./CardOptionFields/PopulationField.vue";
import type { CardQuestion, ChartCard, FunnelStage } from "@/types";
import ColorToggleField from "./CardOptionFields/ColorToggleField.vue";
import { cardCodes, closedQuestion, useAnalysisContext, useCardPopulation } from "./analysisData";
import OptionRow from "./CardOptionFields/OptionRow.vue";
import OptionSection from "./CardOptionFields/OptionSection.vue";

const props = defineProps({
  card: { type: Object, required: true },
});

const card = toRef(props, "card");
const cardsStore = useResultsV2CardsStore();
const { localOptions, updateLocalOption } = useCardLocalOptions(card);
const { availableCards, displayLocale } = useAnalysisContext();
const { color: populationColor } = useCardPopulation(card);

const STAGE_TYPES = [
  "q_single",
  "q_multiple",
  "q_image_choice",
  "q_image_choice_multiple",
  "q_hidden",
  "q_nps",
  "q_rating",
  "q_matrix",
];
const MATRIX_TYPES = ["single", "multiple", "rating", "nps"];
const MAX_STAGES = 10;

const stageCards = computed<ChartCard[]>(() =>
  (availableCards.value as ChartCard[]).filter((c) => {
    if (!STAGE_TYPES.includes(c.type || "") || !c.questions?.length) return false;
    return c.type !== "q_matrix" || !c.specs?.matrix_type || MATRIX_TYPES.includes(c.specs.matrix_type);
  }),
);

const questionOptions = computed(() => stageCards.value.map((c) => ({ value: c.token, label: c.name || c.token })));

const stages = computed<FunnelStage[]>(() => props.card.content?.stages || []);

const stageKey = (stage: FunnelStage) => stage._key || `${stage.question_id}-${stage.label}-${(stage.codes || []).join(".")}`;

function setStages(next: FunnelStage[]) {
  cardsStore.updateCardLocal(props.card.id, { content: { ...props.card.content, stages: next } });
}

function addStage() {
  setStages([
    ...stages.value,
    { card_token: null, question_id: null, codes: [], label: "", _key: `${Date.now()}-${Math.random()}` },
  ]);
}

function removeStage(idx: number) {
  setStages(stages.value.filter((_, i) => i !== idx));
}

function patchStage(idx: number, patch: Partial<FunnelStage>) {
  setStages(stages.value.map((stage, i) => (i === idx ? { ...stage, ...patch } : stage)));
}

function cardFor(stage: FunnelStage): ChartCard | undefined {
  const cards = availableCards.value as ChartCard[];
  if (stage.card_token) return cards.find((c) => c.token === stage.card_token);
  return cards.find((c) => (c.questions || []).some((q) => q.id === stage.question_id));
}

const stageCardToken = (stage: FunnelStage) => cardFor(stage)?.token ?? null;

// A loop repeats each item per iteration, so every item question stays listed.
function itemQuestions(meta: ChartCard | undefined): CardQuestion[] {
  const questions = (meta?.questions || []).filter((q) => q.item_token);
  return questions.length > 1 ? questions : [];
}

function itemOptionsFor(stage: FunnelStage) {
  const questions = itemQuestions(cardFor(stage));
  const titles = questions.map(
    (q, idx) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(q.item_title, displayLocale.value)) || `Item ${idx + 1}`,
  );
  return questions.map((q, idx) => {
    const duplicated = titles.some((title, i) => i !== idx && title === titles[idx]);
    return { value: q.id, label: duplicated && q.name ? `${titles[idx]} (${q.name})` : titles[idx] };
  });
}

function pickCard(idx: number, token: string) {
  const meta = stageCards.value.find((c) => c.token === token);
  const items = itemQuestions(meta);
  patchStage(idx, { card_token: token, question_id: items.length ? null : (closedQuestion(meta)?.id ?? null), codes: [] });
}

function answerOptionsFor(stage: FunnelStage) {
  if (!stage.question_id) return [];
  const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));
  return cardCodes(cardFor(stage), resolveTitle).map(({ code, label }) => ({ value: code, label }));
}

const accentColor = computed({
  get: () => localOptions.value.color || null,
  set: (value: string | null) => updateLocalOption("color", value),
});
</script>

<template>
  <OptionSection
    id="data"
    title="Data">
    <OptionRow
      label="Stages, top to bottom"
      hint="A respondent reaches a stage only if they also match every stage above it, so each conversion is real, not an independent share.">
      <draggable
        :model-value="stages"
        :item-key="stageKey"
        handle=".stage-drag"
        class="space-y-2"
        @update:model-value="setStages($event)">
        <template #item="{ element, index }">
          <div class="space-y-1.5 rounded-md border bg-muted/30 p-2">
            <div class="flex items-center gap-1.5">
              <GripVertical class="stage-drag h-4 w-4 shrink-0 cursor-grab text-muted-foreground active:cursor-grabbing" />
              <span class="text-xs font-medium text-muted-foreground">Stage {{ index + 1 }}</span>
              <Input
                class="h-7 flex-1 text-xs"
                :model-value="element.label"
                placeholder="Label (e.g. Awareness)"
                @update:model-value="patchStage(index, { label: $event })" />
              <button
                type="button"
                class="shrink-0 text-muted-foreground hover:text-foreground"
                aria-label="Remove stage"
                @click="removeStage(index)">
                <X class="h-3.5 w-3.5" />
              </button>
            </div>
            <Select
              :model-value="stageCardToken(element)"
              :options="questionOptions"
              placeholder="Question..."
              filterable
              @update:model-value="pickCard(index, $event)" />
            <Select
              v-if="itemOptionsFor(element).length"
              :model-value="element.question_id"
              :options="itemOptionsFor(element)"
              placeholder="Item..."
              filterable
              @update:model-value="patchStage(index, { question_id: $event })" />
            <Select
              v-if="element.question_id"
              :model-value="element.codes"
              :options="answerOptionsFor(element)"
              placeholder="Answers that count as reaching this stage..."
              multiple
              filterable
              @update:model-value="patchStage(index, { codes: $event })" />
          </div>
        </template>
      </draggable>
      <Button v-if="stages.length < MAX_STAGES" variant="outline" size="sm" class="mt-2 w-full" @click="addStage">
        <Plus class="mr-1 h-3.5 w-3.5" />
        Add stage
      </Button>
    </OptionRow>

    <PopulationField :card="card" />
  </OptionSection>

  <OptionSection
    id="display"
    title="Display"
    :count="accentColor ? 1 : 0">
    <ColorToggleField
      v-model="accentColor"
      :default-color="populationColor"
      hint="Color of the stages. It starts as the color of its population." />
  </OptionSection>
</template>
